import { MouseEvent, useState } from 'react'
import { useDomain } from 'context'
import { useLoginAlert } from 'context/LoginAlertProvider'
import styled, { css } from 'styled-components'
import {
  CompanyInformation_CompanyInformationFragment,
  CompanyInformationNotCachedQuery_CompanyInformationFragment,
  useGqlShareCompanyMutation,
} from 'generated/graphql'
import ChatAppModal from '@app.components/modals/ChatAppModal/ChatAppModal'
import ShareModal from '@app.components/modals/ShareModal'
import { COMPANY_TYPE_LABELS } from '@app.feature/companyInfo/constants/companyType'
import { getLimitNumText } from 'components/ActivityListCardItem/module/getLimitNumText'
import Heart from 'svgIcons/Heart'
import ShareLight from 'svgIcons/ShareLight'
import AnalysisIcon from 'svgIcons/companyInfo/AnalysisIcon'
import CbtIcon from 'svgIcons/companyInfo/CbtIcon'
import ChatIcon from 'svgIcons/companyInfo/ChatIcon'
import { LINKAREER_ACADEMY_BASE_URL } from 'utils/config'
import useToggleCompanyInterest from '../../hooks/useToggleCompanyInterest'

interface IFProps {
  data: Partial<
    CompanyInformation_CompanyInformationFragment &
      CompanyInformationNotCachedQuery_CompanyInformationFragment
  >
}

const CompanyInfoDetailHeader = ({ data }: IFProps) => {
  const { linkareer } = useDomain()
  const { checkLogin } = useLoginAlert()
  const { toggleCompanyInterest } = useToggleCompanyInterest()
  const [shareCompany] = useGqlShareCompanyMutation()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [openShareModal, setOpenShareModal] = useState(false)

  const handleClickLike = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    checkLogin(() => {
      if (data.company?.id) {
        toggleCompanyInterest(data.company.id, data.viewerInterestedIn)
      }
    })
  }

  const handleCloseShareModal = () => {
    setOpenShareModal(false)
  }

  const handleClickShare = () => {
    setOpenShareModal(true)
  }
  const handleClickChatBtn = () => {
    setIsModalOpen(true)
  }
  const handleClickCloseModal = () => {
    setIsModalOpen(false)
  }

  const increaseCompanyShare = () => {
    if (!data.company?.id) {
      return
    }
    shareCompany({
      variables: { companyId: data.company.id },
    })
  }

  return (
    <StyledWrapper>
      <div className="company-info">
        <div className="company-logo">
          <img src={data.thumbnailImage?.url} alt={data.company?.name} />

          {data.isRecruiting && <span className="badge-hiring">채용중</span>}
        </div>

        <div className="company-info-right">
          <div className="company-details">
            <h1>{data.company?.name}</h1>
            <div className="company-type">
              <span>{COMPANY_TYPE_LABELS[data.companyType ?? '']}</span>
              <span>{data.companyIndustry?.secondary}</span>
            </div>
          </div>
          <div className="action-buttons">
            <button
              type="button"
              className="like-button"
              onClick={handleClickLike}
            >
              <Heart
                width={16}
                height={16}
                isFilled={data.viewerInterestedIn}
              />
              {getLimitNumText({
                number: data.company?.interestCount ?? 0,
                limit: 9999,
              })}
            </button>
            <button
              type="button"
              className="share-button"
              onClick={handleClickShare}
            >
              <ShareLight color="#333" />
            </button>
          </div>
        </div>
      </div>
      <div className="link-buttons">
        {data.analysisReportId && (
          <a
            href={`/company-analysis/${data.analysisReportId}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            <AnalysisIcon /> 기업분석자료
          </a>
        )}
        {data.chatRoomId && (
          <button type="button" onClick={handleClickChatBtn}>
            <ChatIcon /> 실시간 채팅
          </button>
        )}

        {data.cbtExamId && (
          <a
            href={`${LINKAREER_ACADEMY_BASE_URL}/exam/detail/${data.cbtExamId}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            <CbtIcon /> 인적성/필기
          </a>
        )}
      </div>
      <ShareModal
        shareLink={`https://${linkareer}/company-info/${data?.id}`}
        open={openShareModal}
        onClose={handleCloseShareModal}
        onClick={increaseCompanyShare}
        ogImage="https://linkareer.com/static/images/company-info.png"
        title={`${data.company?.name} 채용정보 | 자소서·합격후기·면접후기·Q&A - 링커리어`}
      />
      <ChatAppModal
        open={isModalOpen}
        onClose={handleClickCloseModal}
        chatRoomId={data.chatRoomId || ''}
      />
    </StyledWrapper>
  )
}

export default CompanyInfoDetailHeader

const StyledWrapper = styled.header`
  ${({ theme }) => css`
    background-color: ${theme.background.bg_8};
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 16px 20px;
    gap: 16px;

    .company-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .company-logo {
        width: 100px;
        height: 80px;
        padding: 15px 11px;
        border-radius: 5px;
        border: solid 1px ${theme.line.line_3};
        overflow: hidden;
        flex-shrink: 0;
        position: relative;
        > img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .badge-hiring {
          position: absolute;
          top: 10px;
          left: 10px;
          background: #f46161;
          color: ${theme.background.bg_8};
          font-size: 11px;
          font-weight: bold;
          border-radius: 6px;
          min-width: 37px;
          height: 23px;
          display: inline-flex;
          justify-content: center;
          align-items: center;
        }
      }

      .company-info-right {
        flex: 1;
        min-width: 0px;
        .company-details {
          margin-bottom: 5px;

          > h1 {
            font-size: 18px;
            font-weight: bold;
            line-height: 1.11;
            margin-bottom: 3px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .company-type {
            font-size: 12px;
            line-height: 1.67;
            letter-spacing: -0.48px;
            color: ${theme.color.text_2};
            & > span:not(:last-child)::after {
              content: '';
              display: inline-block;
              vertical-align: middle;
              margin: 0 5px;
              width: 1px;
              height: 10px;
              background-color: ${theme.line.line_2};
            }
          }
        }

        .action-buttons {
          display: flex;
          gap: 5px;
          > button {
            background-color: ${theme.background.bg_8};
            border: 1px solid ${theme.line.line_1};
            border-radius: 5px;
            height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            &.like-button {
              min-width: 78px;
              font-size: 13px;
              font-weight: bold;
              gap: 2px;
              color: ${theme.color.text_1};
            }
            &.share-button {
              padding: 6px;
            }
          }
        }
      }
    }

    .link-buttons {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      height: 36px;
      gap: 8px;

      > a,
      button {
        display: flex;
        gap: 1px;
        align-items: center;
        justify-content: center;
        border-radius: 5px;
        background-color: #f2f6ff;
        border: none;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: -0.52px;
        color: ${theme.color.text_1};
        white-space: nowrap;
      }
    }
  `}
`
